import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType } from '@/types';
import { authApi } from '@/lib/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (!token) {
        setLoading(false);
        return;
      }

      // If we have a stored user, use it immediately while verifying with server
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          setUser(user);
        } catch (error) {
          console.error('Error parsing stored user:', error);
        }
      }

      // Verify with server
      const response = await authApi.me();
      if (response.success && response.data) {
        console.log('Auth check successful, updating user state', response.data);
        setUser(response.data);
        // Update stored user with latest data
        localStorage.setItem('user', JSON.stringify(response.data));
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Don't clear storage immediately if we have a stored user
      // This allows offline functionality
      const storedUser = localStorage.getItem('user');
      if (!storedUser) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login({ email, password });
      console.log('Login response:', response);
      if (response.success && response.data) {
        console.log('Login successful, setting user and token:', response.data.employee);
        const { employee, token } = response.data;
        
        // Set token first
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(employee));
        
        // Update user state immediately
        setUser(employee);
        
        console.log('User set successfully:', employee);
        
        // Return success to indicate completion
        return Promise.resolve();
      } else {
        throw new Error(response.error || 'Login failed');
      }
      
    } catch (error: any) {
      console.error('Login error:', error);
      // Clear any potentially stale data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      
      // Improved error handling to extract meaningful error messages
      let errorMessage = 'Login failed. Please check your credentials and try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message && error.message !== 'Login failed') {
        errorMessage = error.message;
      }
      
      // Create an error object that preserves the original error structure
      const authError = new Error(errorMessage);
      (authError as any).response = error.response;
      
      throw authError;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;